import type { Timeseries } from '@cognite/sdk';

import type {
  InfieldTimeseriesGraphQLResponseType,
  InfieldTimeseriesResponseType,
} from '../cdm-types';
import { TIMESERIES_ASSET_EXTERNAL_ID_KEY } from '../timeseries-idm-migrator';

/**
 * Converts a CDM timeseries response from a DMS call to a Timeseries object.
 * @param data - The CDM timeseries response from a DMS call to convert.
 * @returns The Timeseries object.
 */
export const cdmDmsTimeseriesResponseToTimeseries = (
  data: InfieldTimeseriesResponseType
): Timeseries => {
  const assetExternalId = data.assets?.[0]?.externalId;
  const timeseries = cdmTimeseriesResponseToTimeseriesWithoutAssets(data);
  return {
    ...timeseries,
    metadata: {
      ...timeseries.metadata,
      ...(assetExternalId && {
        [TIMESERIES_ASSET_EXTERNAL_ID_KEY]: assetExternalId,
      }),
    },
  };
};

/**
 * Converts a CDM timeseries response from a GraphQL call to a Timeseries object.
 * @param data - The CDM timeseries response from a GraphQL call to convert.
 * @returns The Timeseries object.
 */
export const cdmGraphQLTimeseriesResponseToTimeseries = (
  data: InfieldTimeseriesGraphQLResponseType
): Timeseries => {
  const assetExternalId = data.assets?.items[0]?.externalId;
  const timeseries = cdmTimeseriesResponseToTimeseriesWithoutAssets(data);
  return {
    ...timeseries,
    metadata: {
      ...timeseries.metadata,
      ...(assetExternalId && {
        [TIMESERIES_ASSET_EXTERNAL_ID_KEY]: assetExternalId,
      }),
    },
  };
};

const cdmTimeseriesResponseToTimeseriesWithoutAssets = (
  data:
    | Omit<InfieldTimeseriesGraphQLResponseType, 'assets'>
    | Omit<InfieldTimeseriesResponseType, 'assets'>
): Timeseries => {
  if (!data.externalId || !data.space) {
    throw new Error('Timeseries externalId and space are required');
  }

  const metadata = {
    ...(data.is_highlighted !== undefined && {
      is_highlighted: data.is_highlighted.toString(),
    }),
    ...(data.minVal !== undefined && { minVal: data.minVal.toString() }),
    ...(data.maxVal !== undefined && { maxVal: data.maxVal.toString() }),
  };

  const instanceId = {
    externalId: data.externalId,
    space: data.space,
  };

  return {
    id: 0,
    externalId: data.externalId,
    instanceId,
    name: data.name,
    isString: data.type === 'string',
    metadata,
    unit: data.sourceUnit ?? data.unit?.name,
    isStep: Boolean(data.isStep),
    description: data.description ?? '',
    lastUpdatedTime: new Date(data?.lastUpdatedTime ?? 0),
    createdTime: new Date(data?.createdTime ?? 0),
  };
};
